import { Pitch, AnalysisScores } from '@/types';

// Change to use OpenRouter
const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
const SITE_URL = import.meta.env.VITE_OPENROUTER_SITE_URL || 'http://localhost:5173';
const SITE_NAME = import.meta.env.VITE_OPENROUTER_SITE_NAME || 'Vett AI Platform';

export async function analyzePitch(pitchData: Partial<Pitch>): Promise<AnalysisScores> {
  
  // Check for OpenRouter API key
  if (!OPENROUTER_API_KEY || OPENROUTER_API_KEY === 'your-openrouter-api-key-here') {
    console.warn('OpenRouter API key not set. Using comprehensive analysis.');
    return comprehensiveAnalysis(pitchData);
  }

  try {
    // Change endpoint to OpenRouter
    const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
        'HTTP-Referer': SITE_URL,
        'X-Title': SITE_NAME,
      },
      body: JSON.stringify({
        // Change model to OpenRouter format
        model: "deepseek/deepseek-chat", // or "openai/gpt-3.5-turbo", "google/gemini-pro", etc.
        messages: [
          {
            role: "system",
            content: `You are an extremely harsh VC analyst evaluating pre-seed startups.
            
            Analyze ALL aspects of the pitch:
            1. Problem Clarity (0-100)
            2. Market Viability (0-100)
            3. Feasibility (0-100)
            4. Revenue Logic (0-100)
            5. Execution Readiness (0-100)
            6. Traction Score (0-100)
            7. Competitive Moats (0-100)
            8. Team Strength (0-100)
            
            Also provide:
            - 3-5 specific strengths
            - 3-5 specific weaknesses (be harsh)
            - 2-3 critical red flags
            - 3-5 actionable recommendations
            - Verdict: "reject", "maybe", "meet", or "invest"
            
            Return ONLY valid JSON in this exact format:
            {
              "problemClarity": number,
              "marketViability": number,
              "feasibility": number,
              "revenueLogic": number,
              "executionReadiness": number,
              "tractionScore": number,
              "competitiveMoats": number,
              "teamStrength": number,
              "overall": number,
              "strengths": string[],
              "weaknesses": string[],
              "redFlags": string[],
              "recommendations": string[],
              "verdict": "reject" | "maybe" | "meet" | "invest"
            }`
          },
          {
            role: "user",
            content: `Evaluate this complete pitch deck:
            
            COMPANY: ${pitchData.companyName}
            TAGLINE: ${pitchData.tagline}
            
            PROBLEM: ${pitchData.problem}
            SOLUTION: ${pitchData.solution}
            MARKET: ${pitchData.marketSize}
            BUSINESS MODEL: ${pitchData.businessModel}
            TEAM: ${pitchData.team}
            
            TRACTION:
            - Customer Interviews: ${pitchData.traction?.customerInterviews}
            - Waitlist Signups: ${pitchData.traction?.waitlistSignups}
            - Letters of Intent: ${pitchData.traction?.lettersOfIntent}
            - MVP Status: ${pitchData.traction?.mvpStatus}
            
            COMPETITION:
            - Direct Competitors: ${pitchData.competitive?.directCompetitors?.join(', ')}
            - Competitive Advantage: ${pitchData.competitive?.competitiveAdvantage}
            - Barriers: ${pitchData.competitive?.barriersToEntry?.join(', ')}
            
            FINANCIALS:
            - Funding Ask: $${pitchData.financial?.fundingAsk}
            - Runway: ${pitchData.financial?.runwayMonths} months
            - Burn Rate: $${pitchData.financial?.burnRate}/month
            
            GTM STRATEGY:
            - Channels: ${pitchData.gtm?.customerChannels?.join(', ')}
            - Sales Strategy: ${pitchData.gtm?.salesStrategy}
            
            IP & TECH:
            - Patent Status: ${pitchData.ip?.patentStatus}
            - Development Stage: ${pitchData.ip?.developmentStage}
            - Tech Stack: ${pitchData.ip?.techStack?.join(', ')}
            
            RISKS:
            - Regulatory: ${pitchData.risks?.regulatoryHurdles?.join(', ')}
            - Key Risks: ${pitchData.risks?.keyRisks?.length} identified`
          }
        ],
        temperature: 0.5,
        max_tokens: 1500,
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('OpenRouter API error:', response.status, errorText);
      
      // If API fails (no credits, etc.), fall back to comprehensive analysis
      if (response.status === 402 || response.status === 401) {
        console.warn('OpenRouter API needs credits or auth failed. Using comprehensive analysis.');
        return comprehensiveAnalysis(pitchData);
      }
      
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    
    // Parse the JSON response
    let analysis;
    try {
      // Try to parse the entire response
      analysis = JSON.parse(content);
    } catch {
      // If response has extra text, try to extract JSON
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        analysis = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('No JSON found in response');
      }
    }
    
    // Return the parsed analysis
    return {
      problemClarity: analysis.problemClarity || 50,
      marketViability: analysis.marketViability || 50,
      feasibility: analysis.feasibility || 50,
      revenueLogic: analysis.revenueLogic || 50,
      executionReadiness: analysis.executionReadiness || 50,
      tractionScore: analysis.tractionScore || 50,
      competitiveMoats: analysis.competitiveMoats || 50,
      teamStrength: analysis.teamStrength || 50,
      overall: analysis.overall || 50,
      strengths: analysis.strengths || [],
      weaknesses: analysis.weaknesses || [],
      redFlags: analysis.redFlags || [],
      recommendations: analysis.recommendations || [],
      verdict: analysis.verdict || 'maybe',
    };
  } catch (error) {
    console.error('OpenRouter API error:', error);
    return comprehensiveAnalysis(pitchData);
  }
}

function comprehensiveAnalysis(pitchData: Partial<Pitch>): AnalysisScores {
  
  // ===== BASICS SCORING =====
  const hasProblem = (pitchData.problem?.length || 0) > 200;
  const hasSolution = (pitchData.solution?.length || 0) > 200;
  const hasMarketData = /\d+/.test(pitchData.marketSize || '') && (pitchData.marketSize?.length || 0) > 100;
  const hasBusinessModel = (pitchData.businessModel?.length || 0) > 100;
  const hasTeam = (pitchData.team?.length || 0) > 200;
  
  // ===== TRACTION SCORING =====
  const customerInterviews = pitchData.traction?.customerInterviews || 0;
  const waitlistSignups = pitchData.traction?.waitlistSignups || 0;
  const lettersOfIntent = pitchData.traction?.lettersOfIntent || 0;
  const hasMVP = pitchData.traction?.mvpStatus && pitchData.traction.mvpStatus !== 'none';
  
  // ===== COMPETITION SCORING =====
  const hasCompetitors = (pitchData.competitive?.directCompetitors?.length || 0) > 0;
  const hasCompetitiveAdvantage = (pitchData.competitive?.competitiveAdvantage?.length || 0) > 100;
  const hasBarriers = (pitchData.competitive?.barriersToEntry?.length || 0) > 0;
  
  // ===== FINANCIAL SCORING =====
  const hasFundingAsk = (pitchData.financial?.fundingAsk || 0) > 0;
  const hasRunway = (pitchData.financial?.runwayMonths || 0) > 0;
  const hasRevenue = pitchData.financial?.revenueProjection?.month12 > 0;
  
  // ===== GTM SCORING =====
  const hasChannels = (pitchData.gtm?.customerChannels?.length || 0) > 0;
  const hasStrategy = (pitchData.gtm?.salesStrategy?.length || 0) > 100;
  
  // ===== IP SCORING =====
  const hasPatents = pitchData.ip?.patentStatus && pitchData.ip.patentStatus !== 'none';
  const hasTechStack = (pitchData.ip?.techStack?.length || 0) > 0;
  const hasProprietary = (pitchData.ip?.proprietaryTechnology?.length || 0) > 100;
  
  // ===== RISK SCORING =====
  const hasRiskIdentification = (pitchData.risks?.keyRisks?.length || 0) > 0;
  
  // Calculate scores
  const problemClarity = hasProblem ? 70 + Math.floor(Math.random() * 20) : 30 + Math.floor(Math.random() * 20);
  const marketViability = hasMarketData ? 65 + Math.floor(Math.random() * 25) : 25 + Math.floor(Math.random() * 20);
  const feasibility = hasSolution ? 60 + Math.floor(Math.random() * 25) : 30 + Math.floor(Math.random() * 20);
  const revenueLogic = hasBusinessModel ? 60 + Math.floor(Math.random() * 25) : 25 + Math.floor(Math.random() * 20);
  const executionReadiness = hasTeam ? 70 + Math.floor(Math.random() * 20) : 30 + Math.floor(Math.random() * 20);
  
  // Traction score
  let tractionScore = 20;
  if (customerInterviews >= 50) tractionScore = 90;
  else if (customerInterviews >= 20) tractionScore = 75;
  else if (customerInterviews >= 10) tractionScore = 60;
  else if (customerInterviews > 0) tractionScore = 40;
  
  if (waitlistSignups >= 1000) tractionScore = Math.min(95, tractionScore + 20);
  else if (waitlistSignups >= 500) tractionScore = Math.min(90, tractionScore + 15);
  else if (waitlistSignups >= 100) tractionScore = Math.min(85, tractionScore + 10);
  
  if (hasMVP) tractionScore = Math.min(95, tractionScore + 15);
  if (lettersOfIntent >= 5) tractionScore = Math.min(98, tractionScore + 10);
  
  // Competitive moats
  let competitiveMoats = 30;
  if (hasCompetitors) competitiveMoats += 10;
  if (hasCompetitiveAdvantage) competitiveMoats += 25;
  if (hasBarriers) competitiveMoats += 20;
  if (hasPatents) competitiveMoats += 15;
  
  // Team strength
  let teamStrength = 40;
  if (hasTeam) teamStrength += 30;
  if ((pitchData.team?.length || 0) > 500) teamStrength += 15;
  
  // Overall score (weighted)
  const overall = Math.round(
    (problemClarity * 0.12) +
    (marketViability * 0.15) +
    (feasibility * 0.10) +
    (revenueLogic * 0.13) +
    (executionReadiness * 0.10) +
    (tractionScore * 0.15) +
    (competitiveMoats * 0.10) +
    (teamStrength * 0.15)
  );

  // Generate feedback
  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const redFlags: string[] = [];
  const recommendations: string[] = [];

  if (hasProblem) strengths.push('Well-articulated problem with clear pain points');
  else weaknesses.push('Problem statement needs more clarity and specificity');

  if (hasMarketData) strengths.push('Solid market analysis with TAM/SAM/SOM');
  else weaknesses.push('Market size needs quantification with sources');

  if (customerInterviews >= 20) strengths.push(`Validated with ${customerInterviews}+ customer interviews`);
  else if (customerInterviews > 0) recommendations.push(`Conduct ${20 - customerInterviews} more customer interviews`);
  else redFlags.push('No customer validation demonstrated');

  if (waitlistSignups >= 500) strengths.push(`Strong market interest: ${waitlistSignups}+ waitlist signups`);
  else if (waitlistSignups > 0) recommendations.push('Build marketing momentum to grow waitlist');
  else recommendations.push('Create a landing page to capture interest');

  if (hasCompetitiveAdvantage) strengths.push('Clear competitive differentiation');
  else weaknesses.push('Competitive advantage not clearly defined');

  if (hasFundingAsk && hasRunway) strengths.push('Realistic financial planning with clear runway');
  else weaknesses.push('Financial projections need more detail');

  if (hasRiskIdentification) strengths.push('Thoughtful risk assessment with mitigations');
  else recommendations.push('Add risk analysis to show you\'ve considered challenges');

  // Verdict
  let verdict: 'reject' | 'maybe' | 'meet' | 'invest' = 'reject';
  if (overall >= 70) verdict = 'invest';
  else if (overall >= 55) verdict = 'meet';
  else if (overall >= 40) verdict = 'maybe';

  return {
    problemClarity,
    marketViability,
    feasibility,
    revenueLogic,
    executionReadiness,
    tractionScore,
    competitiveMoats,
    teamStrength,
    overall,
    strengths: strengths.slice(0, 5),
    weaknesses: weaknesses.slice(0, 5),
    redFlags: redFlags.slice(0, 3),
    recommendations: recommendations.slice(0, 5),
    verdict,
  };
}